import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { api } from "@/lib/api";

export type UserRole = "Admin" | "Manager" | "Client";

interface AuthUser {
  userId: string;
  fullName: string;
  role: UserRole;
  accessToken: string;
  idToken: string;
  refreshToken: string;
  email?: string;
  hasPremium?: boolean;
  credits?: number;
  monthlyCreditsLimit?: number;
  stripeId?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionInterval?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

interface SignInPayload {
  username: string;
  password: string;
}

interface SignInResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  role: UserRole;
  userId: string;
  fullName: string;
  email?: string;
}

interface ForceChangePasswordResponse {
  session: string;
}

export type SignInResult =
  | { requiresPasswordChange: false; role: UserRole }
  | { requiresPasswordChange: true; session: string };

interface CompanyData {
  hasPremium?: boolean;
  credits?: number;
  monthlyCreditsLimit?: number;
  stripeId?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionInterval?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  email?: string;
}

export interface SignInWithCognitoPayload {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  userId: string;
  email: string;
  fullName: string;
  role?: UserRole;
}

interface AuthContextValue {
  user: AuthUser | null;
  signIn: (payload: SignInPayload) => Promise<SignInResult>;
  signInWithCognito: (payload: SignInWithCognitoPayload) => void;
  signOut: () => void;
  refreshPremiumStatus: () => Promise<void>;
  updateCredits: (credits: number) => void;
  updateRole: (role: UserRole) => void;
}

const AUTH_KEY = "easly_auth";

function loadFromStorage(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

const STRIPE_API_KEY = import.meta.env.VITE_STRIPE_API_KEY ?? "";

async function fetchCompanyData(
  userId: string,
  role: UserRole,
  token: string
): Promise<CompanyData> {
  if (role !== "Manager") {
    return { hasPremium: false, credits: 0 };
  }
  try {
    const data = await api.get<{
      companyData?: {
        hasPremium?: boolean;
        credits?: number;
        monthlyCreditsLimit?: number;
        stripeId?: string;
        subscriptionId?: string;
        subscriptionStatus?: string;
        subscriptionInterval?: string;
        currentPeriodEnd?: string;
        cancelAtPeriodEnd?: boolean;
      };
      managerData?: { email?: string };
      email?: string;
    }>(`/managers/${userId}`, token, { "stripe-api-key": STRIPE_API_KEY });
    return {
      hasPremium: data.companyData?.hasPremium ?? false,
      credits: data.companyData?.credits ?? 0,
      monthlyCreditsLimit: data.companyData?.monthlyCreditsLimit ?? 0,
      stripeId: data.companyData?.stripeId ?? null,
      subscriptionId: data.companyData?.subscriptionId,
      subscriptionStatus: data.companyData?.subscriptionStatus,
      subscriptionInterval: data.companyData?.subscriptionInterval,
      currentPeriodEnd: data.companyData?.currentPeriodEnd,
      cancelAtPeriodEnd: data.companyData?.cancelAtPeriodEnd ?? false,
      email: data.managerData?.email ?? data.email ?? null,
    };
  } catch {
    return { hasPremium: false, credits: 0 };
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(loadFromStorage);

  const persistUser = useCallback((updated: AuthUser) => {
    localStorage.setItem(AUTH_KEY, JSON.stringify(updated));
    setUser(updated);
  }, []);

  // Refresh premium/credits on mount so stale localStorage never shows wrong plan
  useEffect(() => {
    const stored = loadFromStorage();
    if (stored?.role === "Manager") {
      fetchCompanyData(stored.userId, stored.role, stored.accessToken).then((companyData) => {
        // If a 401 occurred, api.ts already cleared localStorage — don't write the expired token back
        if (loadFromStorage() !== null) {
          persistUser({ ...stored, ...companyData });
        }
      });
    }
  }, [persistUser]);

  const signIn = useCallback(
    async (payload: SignInPayload): Promise<SignInResult> => {
      const data = await api.post<SignInResponse | ForceChangePasswordResponse>(
        "/auth/sign-in",
        payload
      );

      if ("session" in data) {
        return { requiresPasswordChange: true, session: data.session };
      }

      const authUser: AuthUser = {
        userId: data.userId,
        fullName: data.fullName,
        role: data.role,
        accessToken: data.accessToken,
        idToken: data.idToken,
        refreshToken: data.refreshToken,
        // payload.username is the Cognito email — use as fallback if backend doesn't return email
        email: data.email ?? payload.username,
      };

      persistUser(authUser);

      // Fetch premium/credits in background — update stored user when ready
      fetchCompanyData(data.userId, data.role, data.accessToken).then((companyData) => {
        persistUser({ ...authUser, ...companyData });
      });

      return { requiresPasswordChange: false, role: authUser.role };
    },
    [persistUser]
  );

  const signInWithCognito = useCallback(
    (payload: SignInWithCognitoPayload) => {
      const authUser: AuthUser = {
        userId: payload.userId,
        fullName: payload.fullName || payload.email,
        role: payload.role ?? "Client",
        accessToken: payload.accessToken,
        idToken: payload.idToken,
        refreshToken: payload.refreshToken,
        email: payload.email,
      };
      persistUser(authUser);
    },
    [persistUser]
  );

  const signOut = useCallback(() => {
    localStorage.removeItem(AUTH_KEY);
    setUser(null);
  }, []);

  const refreshPremiumStatus = useCallback(async () => {
    if (!user) return;
    const companyData = await fetchCompanyData(user.userId, user.role, user.accessToken);
    if (loadFromStorage() !== null) {
      persistUser({ ...user, ...companyData });
    }
  }, [user, persistUser]);

  const updateCredits = useCallback(
    (credits: number) => {
      if (!user) return;
      persistUser({ ...user, credits });
    },
    [user, persistUser]
  );

  const updateRole = useCallback(
    (role: UserRole) => {
      if (!user) return;
      persistUser({ ...user, role });
    },
    [user, persistUser]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signInWithCognito,
        signOut,
        refreshPremiumStatus,
        updateCredits,
        updateRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
